import { UtilityService } from "@eforall/common";
import { AcceleratorFuncData, ATTENDANCE_SCORE_WEIGHT, E_LEARNING_SCORE_WEIGHT, FINAL_PRESENTATION_SCORE_WEIGHT, ROADMAP_SCORE_WEIGHT, SURVEY_SCORE_WEIGHT } from "@interfaces";

const COMPLETION_POINTS = 1;
const ON_TIME_BONUS = 1;

interface AttendanceAndSurveyScore {
	attendanceScore: number,
	surveyScore: number,
	absences: number,
}


export async function calculateGraduationScore(util: UtilityService, data: AcceleratorFuncData) {


	const totalScoresWeight = E_LEARNING_SCORE_WEIGHT + SURVEY_SCORE_WEIGHT + ATTENDANCE_SCORE_WEIGHT + ROADMAP_SCORE_WEIGHT + FINAL_PRESENTATION_SCORE_WEIGHT;
	if (totalScoresWeight !== 1) throw new Error(`Weights must add up to 1. Current total: ${totalScoresWeight}`);


	//
	// Get the past Events based on event status and then filter the sessions to just the ones that have happened already
	//
	const pastEventIds = data.events.filter(e => e.status === 'Past').map(e => e.eventId);
	const pastEventIdMap = util.array.toBooleanMap(pastEventIds);
	const pastSessionIds = (data.sessions ?? []).filter(s => pastEventIdMap[s.eventId]).map(s => s.accSessionId);


	//
	// If we have no past sessionIds then all participation values will be zero.
	// so, we can just return an empty map here.
	//
	if (!pastSessionIds.length) return;

	// Calculate attendance, survey and eLearning score for each person
	const attendaceAndSurveyScore = calculateAttendanceAndSurveyScoreForPerson(util, pastSessionIds, data);
	const eLearningScore = calculateELearningScore(util, data);


	data.entrepreneurs.map(e => {
		const attendanceAndSurvey = attendaceAndSurveyScore[e.personId] ?? { attendanceScore: 0, surveyScore: 0, absences: undefined };

		e.attendanceScore = attendanceAndSurvey.attendanceScore;
		e.surveyScore = attendanceAndSurvey.surveyScore;
		e.eLearningScore = eLearningScore[e.personId] ?? 0;
		e.absences = attendanceAndSurvey.absences;
		e.eLearningCompletionPercentage = data.courseEnrollments.find(ce => ce.personId == e.personId)?.percentComplete ?? 0;
	});
}


function calculateAttendanceAndSurveyScoreForPerson(util: UtilityService, pastSessionIds: number[], data: AcceleratorFuncData): Record<number, AttendanceAndSurveyScore> {
	//
	// Make a boolean map for quick lookup and then filter the attendees to only past sessions
	//
	const pastSessionIdMap = util.array.toBooleanMap(pastSessionIds);
	const pastAttendees = data.sessionAttendees.filter(a => pastSessionIdMap[a.accSessionId]);


	//
	// Get the set of attendees for each personId and put it in a map of arrays
	//
	const pastAttendeesByPersonId = util.array.toArrayMap(pastAttendees, a => a.personId);

	//
	// Make a boolean map of which attendees have taken a survey
	//
	const surveyResponseByAttendeeId = util.array.toBooleanMap(data.sessionSurveyResponses.map(r => r.accSessionAttendeeId));

	const totalPastSessions = pastSessionIds.length;

	//
	// Now, for each person we can calculate attendance and survey score based on the number of past sessions
	//
	const result: Record<number, AttendanceAndSurveyScore> = {};
	Object.entries(pastAttendeesByPersonId).forEach(([personId, attendees]) => {

		let sessionsAttended = 0, surveysTaken = 0, absences = 0;

		attendees.forEach(a => {
			if (a.status == 'Absent') absences++;
			if (a.status == 'Present') sessionsAttended++;
			if (surveyResponseByAttendeeId[a.accSessionAttendeeId]) surveysTaken++;
		});

		result[Number(personId)] = {
			attendanceScore: sessionsAttended / totalPastSessions * 100,
			surveyScore: surveysTaken / totalPastSessions * 100,
			absences,
		};
	});

	return result;
}


function calculateELearningScore(util: UtilityService, data: AcceleratorFuncData): Record<number, number> {

	// Filter courseLevels to just the ones that are past
	const now = Date.now() / 1000;
	const pastCourseLevels = data.accSessionCourseLevelsInfo.filter(i => i.dueDateUTC <= now);

	if (!pastCourseLevels.length) return {};

	const totalPossiblePoints = pastCourseLevels.length * (COMPLETION_POINTS + ON_TIME_BONUS);

	const pastCourseLevelIdMap = util.array.toMap(pastCourseLevels, i => i.courseLevelId);

	const result: Record<number, number> = {};

	// Now, for each person we can calculate eLearning score based on the number of past course levels completed and whether they were completed on time
	data.courseEnrollments.forEach(e => {

		let score = 0;

		e.progress.forEach(p => {
			const courseLevelInfo = pastCourseLevelIdMap[p.courseLevelId];
			if (courseLevelInfo && p.completedUTC) {
				score += COMPLETION_POINTS;
				if (p.completedUTC <= courseLevelInfo.dueDateUTC) {
					score += ON_TIME_BONUS;
				}
			}
		})

		result[e.personId] = score / totalPossiblePoints * 100;
	});

	return result;
}